import * as React from 'react';
import { clsx } from 'clsx';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';

export interface PaginationProps {
  /**
   * Current page number (1-indexed)
   */
  currentPage: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const containerStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const buttonStyles: React.CSSProperties = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  flexShrink: 0,
  WebkitTapHighlightColor: 'transparent',
  outline: 'none',
};

const buttonDisabledStyles: React.CSSProperties = {
  cursor: 'not-allowed',
  opacity: 0.5,
};

const labelStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '1.5',
  color: '#2f2f2f',
};

const inputWrapperStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const inputStyles: React.CSSProperties = {
  width: '60px',
  paddingTop: '12px',
  paddingBottom: '12px',
  paddingLeft: '16px',
  paddingRight: '16px',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '1.5',
  color: '#2f2f2f',
  backgroundColor: 'white',
  border: '1px solid #dfdfdf',
  borderRadius: '8px',
  fontFamily: 'inherit',
  textAlign: 'center',
  outline: 'none',
};

const inputFocusStyles: React.CSSProperties = {
  boxShadow: '0px 0px 0px 3px #3cad51',
};

const textStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '1.5',
  color: '#2f2f2f',
};

/**
 * Pagination component - Arbor Design System
 *
 * A pagination control with first/previous/next/last buttons and a page input field.
 */
export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState(currentPage.toString());
    const [focusedButton, setFocusedButton] = React.useState<string | null>(null);
    const [hoveredButton, setHoveredButton] = React.useState<string | null>(null);
    const [activeButton, setActiveButton] = React.useState<string | null>(null);
    const [inputFocused, setInputFocused] = React.useState(false);

    React.useEffect(() => {
      setInputValue(currentPage.toString());
    }, [currentPage]);

    const handleFirstPage = () => {
      if (currentPage !== 1) {
        onPageChange(1);
      }
    };

    const handlePreviousPage = () => {
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    };

    const handleLastPage = () => {
      if (currentPage !== totalPages) {
        onPageChange(totalPages);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Allow empty or valid numbers
      if (value === '' || /^\d+$/.test(value)) {
        setInputValue(value);
      }
    };

    const handleInputBlur = () => {
      setInputFocused(false);
      const pageNumber = parseInt(inputValue, 10);
      if (isNaN(pageNumber) || pageNumber < 1) {
        setInputValue(currentPage.toString());
      } else if (pageNumber > totalPages) {
        setInputValue(totalPages.toString());
        onPageChange(totalPages);
      } else if (pageNumber !== currentPage) {
        onPageChange(pageNumber);
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleInputBlur();
        (e.target as HTMLInputElement).blur();
      }
    };

    const getButtonStyle = (buttonName: string, disabled: boolean): React.CSSProperties => {
      if (disabled) {
        return { ...buttonStyles, ...buttonDisabledStyles };
      }

      const isHovered = hoveredButton === buttonName;
      const isFocused = focusedButton === buttonName;
      const isActive = activeButton === buttonName;

      const style: React.CSSProperties = {
        ...buttonStyles,
      };

      // Apply hover state (light gray background)
      if (isHovered && !isFocused && !isActive) {
        style.backgroundColor = '#efefef';
      }

      // Apply active state (same as hover or transparent)
      if (isActive && !isFocused) {
        style.backgroundColor = 'transparent';
      }

      // Apply focus state (green ring, transparent background)
      if (isFocused) {
        style.boxShadow = '0px 0px 0px 3px #3cad51';
        style.backgroundColor = 'rgba(255, 255, 255, 0.01)';
      }

      return style;
    };

    const isFirstDisabled = currentPage === 1;
    const isPreviousDisabled = currentPage === 1;
    const isNextDisabled = currentPage === totalPages;
    const isLastDisabled = currentPage === totalPages;

    return (
      <div
        ref={ref}
        className={clsx('arbor-pagination', className)}
        style={{ ...containerStyles, ...style }}
        data-testid={dataTestId}
      >
        {/* First page button */}
        <button
          type="button"
          onClick={handleFirstPage}
          disabled={isFirstDisabled}
          onMouseEnter={() => setHoveredButton('first')}
          onMouseLeave={() => setHoveredButton(null)}
          onMouseDown={() => setActiveButton('first')}
          onMouseUp={() => setActiveButton(null)}
          onFocus={() => setFocusedButton('first')}
          onBlur={() => setFocusedButton(null)}
          style={getButtonStyle('first', isFirstDisabled)}
          aria-label="Go to first page"
        >
          <ChevronsLeft size={16} color="#2f2f2f" />
        </button>

        {/* Previous page button */}
        <button
          type="button"
          onClick={handlePreviousPage}
          disabled={isPreviousDisabled}
          onMouseEnter={() => setHoveredButton('previous')}
          onMouseLeave={() => setHoveredButton(null)}
          onMouseDown={() => setActiveButton('previous')}
          onMouseUp={() => setActiveButton(null)}
          onFocus={() => setFocusedButton('previous')}
          onBlur={() => setFocusedButton(null)}
          style={getButtonStyle('previous', isPreviousDisabled)}
          aria-label="Go to previous page"
        >
          <ChevronLeft size={16} color="#2f2f2f" />
        </button>

        {/* Page input */}
        <div style={inputWrapperStyles}>
          <span style={labelStyles}>Page</span>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={() => setInputFocused(true)}
            onKeyDown={handleInputKeyDown}
            style={{
              ...inputStyles,
              ...(inputFocused && inputFocusStyles),
            }}
            aria-label={`Page ${currentPage} of ${totalPages}`}
          />
          <span style={textStyles}>of {totalPages}</span>
        </div>

        {/* Next page button */}
        <button
          type="button"
          onClick={handleNextPage}
          disabled={isNextDisabled}
          onMouseEnter={() => setHoveredButton('next')}
          onMouseLeave={() => setHoveredButton(null)}
          onMouseDown={() => setActiveButton('next')}
          onMouseUp={() => setActiveButton(null)}
          onFocus={() => setFocusedButton('next')}
          onBlur={() => setFocusedButton(null)}
          style={getButtonStyle('next', isNextDisabled)}
          aria-label="Go to next page"
        >
          <ChevronRight size={16} color="#2f2f2f" />
        </button>

        {/* Last page button */}
        <button
          type="button"
          onClick={handleLastPage}
          disabled={isLastDisabled}
          onMouseEnter={() => setHoveredButton('last')}
          onMouseLeave={() => setHoveredButton(null)}
          onMouseDown={() => setActiveButton('last')}
          onMouseUp={() => setActiveButton(null)}
          onFocus={() => setFocusedButton('last')}
          onBlur={() => setFocusedButton(null)}
          style={getButtonStyle('last', isLastDisabled)}
          aria-label="Go to last page"
        >
          <ChevronsRight size={16} color="#2f2f2f" />
        </button>
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';
